import React, { useEffect, useMemo, useState } from 'react'
import { getAllProducts, updateProduct } from '../services/productService'
import {
  Banner,
  Button,
  Drawer,
  EmptyState,
  Field,
  IconButton,
  Meter,
  Pagination,
  Pill,
  RowActions,
  SearchInput,
  SkeletonRows,
  StatTiles,
  Tabs,
  Thumb,
  Toast,
  formatRWF,
  useFlash,
  usePagination,
} from './AdminUI'

const STATUS = {
  out: { label: 'Out of stock', tone: 'red', meter: 'red' },
  low: { label: 'Low stock', tone: 'amber', meter: 'amber' },
  ok: { label: 'Healthy', tone: 'green', meter: 'green' },
}

const STATE_ORDER = { out: 0, low: 1, ok: 2 }

const QUICK_ADJUST = [-10, -1, 1, 5, 10, 50]

function InventoryManagement({ onManageProducts }) {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useFlash()
  const [filter, setFilter] = useState('alerts')
  const [search, setSearch] = useState('')
  const [savingId, setSavingId] = useState(null)
  const [activeId, setActiveId] = useState(null)
  const [stockDraft, setStockDraft] = useState('')
  const [thresholdDraft, setThresholdDraft] = useState('')

  const loadProducts = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getAllProducts()
      setProducts(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err.message || 'Failed to load inventory')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadProducts()
  }, [])

  const withFlags = useMemo(
    () =>
      products.map((p) => {
        const stock = Number(p.stock) || 0
        const threshold = Number(p.lowStockThreshold) || 10
        const state = stock === 0 ? 'out' : stock <= threshold ? 'low' : 'ok'
        const fill = Math.min(100, Math.round((stock / Math.max(threshold * 3, 1)) * 100))
        return { ...p, stock, threshold, state, alert: state !== 'ok', fill }
      }),
    [products]
  )

  const counts = useMemo(
    () => ({
      total: withFlags.length,
      out: withFlags.filter((p) => p.state === 'out').length,
      low: withFlags.filter((p) => p.state === 'low').length,
      ok: withFlags.filter((p) => p.state === 'ok').length,
      alerts: withFlags.filter((p) => p.alert).length,
      units: withFlags.reduce((sum, p) => sum + p.stock, 0),
      value: withFlags.reduce((sum, p) => sum + p.stock * (Number(p.priceNew) || 0), 0),
    }),
    [withFlags]
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return withFlags
      .filter((p) => {
        if (filter === 'alerts' && !p.alert) return false
        if (['out', 'low', 'ok'].includes(filter) && p.state !== filter) return false
        if (!q) return true
        return [p.title, p.name, p.category, p.badge].filter(Boolean).join(' ').toLowerCase().includes(q)
      })
      .sort(
        (a, b) =>
          STATE_ORDER[a.state] - STATE_ORDER[b.state] ||
          String(a.title || a.name || '').localeCompare(String(b.title || b.name || ''))
      )
  }, [withFlags, filter, search])

  const pager = usePagination(filtered, 10, `${filter}|${search}`)

  const active = useMemo(
    () => withFlags.find((p) => (p.id || p._id) === activeId) || null,
    [withFlags, activeId]
  )

  const openAdjust = (product) => {
    setActiveId(product.id || product._id)
    setStockDraft(String(product.stock))
    setThresholdDraft(String(product.threshold))
    setError('')
  }

  const saveProduct = async (product, patch, message) => {
    const id = product.id || product._id
    setSavingId(id)
    setError('')
    try {
      await updateProduct(id, patch)
      setProducts((prev) => prev.map((p) => ((p.id || p._id) === id ? { ...p, ...patch } : p)))
      setSuccess(message)
      return true
    } catch (err) {
      setError(err.message || 'Failed to update inventory')
      return false
    } finally {
      setSavingId(null)
    }
  }

  const adjustBy = (product, delta) => {
    const next = Math.max(0, product.stock + delta)
    if (next === product.stock) return
    saveProduct(
      product,
      { stock: next },
      `${product.title || product.name}: ${product.stock} → ${next} units`
    )
    if ((product.id || product._id) === activeId) setStockDraft(String(next))
  }

  const saveDrawer = async () => {
    if (!active) return
    const patch = {}
    const nextStock = Math.max(0, parseInt(stockDraft, 10) || 0)
    const nextThreshold = Math.max(0, parseInt(thresholdDraft, 10) || 0)
    if (nextStock !== active.stock) patch.stock = nextStock
    if (nextThreshold !== active.threshold) patch.lowStockThreshold = nextThreshold
    if (!Object.keys(patch).length) {
      setActiveId(null)
      return
    }
    const ok = await saveProduct(active, patch, `Inventory updated for ${active.title || active.name}`)
    if (ok) setActiveId(null)
  }

  const stats = [
    { id: 'alerts', label: 'Needs attention', value: counts.alerts, hint: 'Low or out of stock', icon: 'fa-triangle-exclamation', tone: 'amber', filter: 'alerts' },
    { id: 'out', label: 'Out of stock', value: counts.out, hint: 'Not purchasable', icon: 'fa-circle-xmark', tone: 'red', filter: 'out' },
    { id: 'ok', label: 'Healthy', value: counts.ok, hint: 'Above threshold', icon: 'fa-circle-check', tone: 'green', filter: 'ok' },
    { id: 'units', label: 'Units on hand', value: counts.units.toLocaleString(), hint: `Value ${formatRWF(counts.value)}`, icon: 'fa-boxes-stacked', tone: 'blue', filter: 'all' },
  ]

  const draftStock = Math.max(0, parseInt(stockDraft, 10) || 0)
  const draftThreshold = Math.max(0, parseInt(thresholdDraft, 10) || 0)
  const draftState = draftStock === 0 ? 'out' : draftStock <= draftThreshold ? 'low' : 'ok'
  const busy = Boolean(savingId)

  return (
    <div className="ax">
      <StatTiles items={stats} active={filter} onSelect={setFilter} />

      {error && !active ? <Banner onClose={() => setError('')}>{error}</Banner> : null}

      <div className="ax-card">
        <div className="ax-toolbar">
          <SearchInput value={search} onChange={setSearch} placeholder="Search product or category…" label="Search inventory" />
          <Tabs
            value={filter}
            onChange={setFilter}
            label="Inventory filters"
            options={[
              { id: 'alerts', label: 'Alerts', count: counts.alerts },
              { id: 'out', label: 'Out', count: counts.out },
              { id: 'low', label: 'Low', count: counts.low },
              { id: 'ok', label: 'Healthy', count: counts.ok },
              { id: 'all', label: 'All', count: counts.total },
            ]}
          />
          <span className="ax-toolbar__grow" />
          <IconButton
            icon={loading ? 'fa-spinner fa-spin' : 'fa-rotate'}
            label="Refresh inventory"
            className="ax-icon-btn--outline"
            onClick={loadProducts}
            disabled={loading}
          />
          {onManageProducts ? (
            <Button icon="fa-box" onClick={onManageProducts}>
              Manage catalog
            </Button>
          ) : null}
        </div>

        {loading && products.length === 0 ? (
          <SkeletonRows cols={5} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={filter === 'alerts' && !search ? 'fa-circle-check' : 'fa-boxes-stacked'}
            title={search ? 'No matching products' : filter === 'alerts' ? 'Stock levels look healthy' : 'Nothing in this view'}
            text={
              search
                ? 'Try another search term.'
                : filter === 'alerts'
                  ? 'No products are low or out of stock right now.'
                  : 'Switch filters to see more products.'
            }
            action={
              filter !== 'all' ? (
                <Button onClick={() => setFilter('all')}>Show all products</Button>
              ) : null
            }
          />
        ) : (
          <>
            <div className="ax-table-wrap">
              <table className="ax-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th style={{ width: 240 }}>Stock level</th>
                    <th>Status</th>
                    <th className="is-center">Quick adjust</th>
                    <th className="is-right">
                      <span className="ax-sr">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pager.pageItems.map((product) => {
                    const id = product.id || product._id
                    const rowBusy = savingId === id
                    return (
                      <tr key={id} className={rowBusy ? 'is-busy' : ''}>
                        <td>
                          <div className="ax-cell">
                            <Thumb src={product.image} />
                            <div className="ax-cell__text">
                              <strong>{product.title || product.name}</strong>
                              <small>{product.category || product.badge || 'Uncategorised'}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="ax-stock">
                            <span className="ax-stock__num">
                              <strong>{product.stock}</strong> units
                              <em>alert at {product.threshold}</em>
                            </span>
                            <Meter value={product.fill} tone={STATUS[product.state].meter} />
                          </div>
                        </td>
                        <td>
                          <Pill tone={STATUS[product.state].tone}>{STATUS[product.state].label}</Pill>
                        </td>
                        <td className="is-center">
                          <div className="ax-stepper">
                            <button
                              type="button"
                              aria-label={`Remove 1 from ${product.title}`}
                              disabled={busy || product.stock <= 0}
                              onClick={() => adjustBy(product, -1)}
                            >
                              <i className="fas fa-minus" aria-hidden="true" />
                            </button>
                            <span>{rowBusy ? <i className="fas fa-spinner fa-spin" aria-hidden="true" /> : product.stock}</span>
                            <button
                              type="button"
                              aria-label={`Add 1 to ${product.title}`}
                              disabled={busy}
                              onClick={() => adjustBy(product, 1)}
                            >
                              <i className="fas fa-plus" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                        <td className="is-right">
                          <RowActions
                            label={`Actions for ${product.title || product.name}`}
                            title={product.title || product.name}
                            subtitle={`${product.stock} units · ${STATUS[product.state].label} · alert at ${product.threshold}`}
                            media={<Thumb src={product.image} />}
                            actions={[
                              {
                                id: 'adjust',
                                label: 'Adjust stock & alert level',
                                description: 'Set an exact count or change the low-stock threshold',
                                icon: 'fa-sliders',
                                onClick: () => openAdjust(product),
                              },
                              {
                                id: 'restock-10',
                                label: 'Restock +10 units',
                                description: `New stock will be ${product.stock + 10}`,
                                icon: 'fa-box-open',
                                tone: 'success',
                                disabled: busy,
                                onClick: () => adjustBy(product, 10),
                              },
                              {
                                id: 'restock-50',
                                label: 'Restock +50 units',
                                description: `New stock will be ${product.stock + 50}`,
                                icon: 'fa-boxes-stacked',
                                tone: 'success',
                                disabled: busy,
                                onClick: () => adjustBy(product, 50),
                              },
                              {
                                id: 'zero',
                                label: 'Mark as out of stock',
                                description: 'Set stock to 0 so customers can’t order it',
                                icon: 'fa-ban',
                                tone: 'warning',
                                disabled: busy || product.stock <= 0,
                                onClick: () => adjustBy(product, -product.stock),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <Pagination pager={pager} label="products" />
          </>
        )}
      </div>

      <Drawer
        open={Boolean(active)}
        onClose={() => setActiveId(null)}
        width={480}
        title="Adjust inventory"
        subtitle={active ? active.title || active.name : ''}
        footer={
          active ? (
            <>
              {onManageProducts ? (
                <Button
                  variant="ghost"
                  icon="fa-box"
                  onClick={() => {
                    setActiveId(null)
                    onManageProducts()
                  }}
                >
                  Open catalog
                </Button>
              ) : null}
              <span className="ax-foot__spacer" />
              <Button onClick={() => setActiveId(null)}>Cancel</Button>
              <Button variant="primary" icon="fa-check" loading={busy} disabled={busy || stockDraft === ''} onClick={saveDrawer}>
                Save changes
              </Button>
            </>
          ) : null
        }
      >
        {active ? (
          <>
            {error ? <Banner onClose={() => setError('')}>{error}</Banner> : null}

            <div className="ax-product-head">
              <Thumb src={active.image} />
              <div className="ax-cell__text">
                <strong>{active.title || active.name}</strong>
                <small>{active.category || 'Uncategorised'} · {formatRWF(active.priceNew)}</small>
              </div>
              <Pill tone={STATUS[active.state].tone}>{STATUS[active.state].label}</Pill>
            </div>

            <div className="ax-kv-grid">
              <div className="ax-kv">
                <small>Current stock</small>
                <strong>{active.stock} units</strong>
              </div>
              <div className="ax-kv">
                <small>After saving</small>
                <strong className={draftStock !== active.stock ? 'ax-text-blue' : ''}>
                  {draftStock} units
                  {draftStock !== active.stock ? (
                    <em className={`ax-delta ${draftStock > active.stock ? 'is-up' : 'is-down'}`}>
                      {draftStock > active.stock ? '+' : ''}
                      {draftStock - active.stock}
                    </em>
                  ) : null}
                </strong>
              </div>
            </div>

            <div className="ax-panel">
              <div className="ax-panel__head">
                <h3>
                  <i className="fas fa-bolt" aria-hidden="true" /> Quick adjust
                </h3>
                <span className="ax-sub">Changes the value below</span>
              </div>
              <div className="ax-panel__body">
                <div className="ax-chips">
                  {QUICK_ADJUST.map((delta) => (
                    <button
                      key={delta}
                      type="button"
                      className={`ax-chip ${delta < 0 ? 'is-neg' : ''}`}
                      onClick={() => setStockDraft(String(Math.max(0, draftStock + delta)))}
                    >
                      {delta > 0 ? `+${delta}` : delta}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="ax-form-grid">
              <Field label="Stock quantity" htmlFor="inv-stock" hint="Exact units on hand">
                <input
                  id="inv-stock"
                  type="number"
                  min="0"
                  className="ax-input"
                  value={stockDraft}
                  onChange={(e) => setStockDraft(e.target.value)}
                  disabled={busy}
                />
              </Field>
              <Field label="Low-stock alert at" htmlFor="inv-threshold" hint="Alert when stock reaches this">
                <input
                  id="inv-threshold"
                  type="number"
                  min="0"
                  className="ax-input"
                  value={thresholdDraft}
                  onChange={(e) => setThresholdDraft(e.target.value)}
                  disabled={busy}
                />
              </Field>
            </div>

            <div className={`ax-callout ${draftState === 'ok' ? '' : draftState === 'low' ? 'ax-callout--amber' : 'ax-callout--red'}`}>
              <i
                className={`fas ${draftState === 'ok' ? 'fa-circle-check' : draftState === 'low' ? 'fa-triangle-exclamation' : 'fa-circle-xmark'}`}
                aria-hidden="true"
              />
              {draftState === 'ok'
                ? 'Stock will be healthy after this change.'
                : draftState === 'low'
                  ? 'Stock will still be at or below the alert level.'
                  : 'Product will show as out of stock in the shop.'}
            </div>
          </>
        ) : null}
      </Drawer>

      <Toast message={success} />
    </div>
  )
}

export default InventoryManagement
